import { useState } from "react";
import { Box, Grid, MenuItem, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { addMonths, format, startOfMonth } from "date-fns";
import { getItems } from "../../../api/items";
import { useRecords } from "../../../api/useRecords";
import { monthlyCountsFromGroupedRows, monthlyPointsFromCountMaps, singleCount } from "../../../dashboard-framework/aggregateRows";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import {
    CLIENT_TYPE_OPTIONS,
    sessionClientTypeFilterByOption,
    treatmentClientTypeFilterByOption,
    treatmentMatchesClientType,
} from "../../../dashboard-framework/commonFilters";
import { resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { drillDownFromAggregate } from "../../../dashboard-framework/drilldown";
import { formatQuantity } from "../../../dashboard-framework/format";
import {
    describeSessionsCount,
    describeTreatmentsCount,
    treatmentStatusListPt,
    TREATMENT_STATUS_LABELS_PT,
} from "../../../dashboard-framework/describeWidgetLogic";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { PivotNode } from "../../../dashboard-framework/pivot";
import { DrillDownColumn, DrillDownConfig } from "../../../dashboard-framework/types";
import { useAggregate } from "../../../dashboard-framework/useAggregate";
import { useGlobalFilterValues } from "../../../dashboard-framework/useGlobalFilterValues";
import { AreaChart } from "../../../dashboard-framework/widgets/AreaChart";
import { DataTableCard } from "../../../dashboard-framework/widgets/DataTableCard";
import { PivotTableCard } from "../../../dashboard-framework/widgets/PivotTableCard";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { TimeSeriesChart } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import { COARSE_DISORDER_GROUPS } from "../shared/disorderGroups";
import type { ClientTypeKey, CoarseDisorderGroupKey } from "../shared/disorderGroups";
import { formatDrillDate, treatmentRoute, TREATMENTS_DRILL_FIELDS, treatmentsDrillColumns } from "../shared/drillColumns";
import {
    activeTreatmentsQuery,
    avgSessionsSeries,
    DECLINED_STATUS,
    finishedMonthlyQuery,
    FINISHED_STATUSES,
    futureSessionsByServiceQuery,
    sessionsPerFinishedTreatmentQuery,
    sessionsTreatmentsGlobalFilters,
    startedFinishedSeries,
    startedMonthlyQuery,
    SUCCESS_STATUSES,
    successSeries,
    treatmentsTotalQuery,
} from "./sessionsTreatmentsConfig";
import {
    buildFinishedTreatmentAnalysis,
    FinishedTreatmentItem,
    futureSessionsPivotRows,
    partnerNameRows,
    partnerSpecializationRows,
    sessionsByTreatment,
} from "./sessionsTreatmentsRows";
import {
    ACTIVES_MEASURE_KEY,
    activeTreatmentsOf,
    finishedTreatmentsOf,
    OutcomeDrillRow,
    OutcomeFilters,
    OutcomeTreatmentItem,
    outcomeDrillRowOf,
    outcomePivot,
    outcomePivotItems,
} from "./sessionsTreatmentsOutcomeRows";

const ALL_OPTION_VALUE = "__all__";

// Campos do fetch da "Análise global de tratamentos terminados": atribuição pela gestora de caso (dono
// do tratamento), grupo de perturbação (diagnose_main), tipo de cliente (age_when_started) e sessões
// aninhadas (serviço + estado) para a média de sessões realizadas. Lido com o token do visualizador
// (mesmo padrão dos outros fetches raw deste dashboard — audiência de direção com leitura total).
const OUTCOME_TREATMENT_FIELDS = [
    "id",
    "treatment_status_new",
    "age_when_started",
    "date_of_start",
    "date_if_finish",
    "diagnose_main.id",
    "diagnose_main.name",
    "client.client_id",
    "client_owner.id",
    "client_owner.user_id.id",
    "client_owner.user_id.first_name",
    "client_owner.user_id.last_name",
    "sessions.service",
    "sessions.status",
].join(",");

const OUTCOME_CLIENT_TYPE_OPTIONS = [
    { value: "adults", label: "Adultos" },
    { value: "children", label: "Crianças" },
];

type ClientTypeSelectProps = {
    value: string | null;
    onChange: (newValue: string | null) => void;
};

const ClientTypeSelect = ({ value, onChange }: ClientTypeSelectProps) => {
    return (
        <TextField
            select
            size="small"
            label="Tipo de cliente"
            value={value === null ? ALL_OPTION_VALUE : value}
            onChange={(event) => {
                const newValue = event.target.value;
                onChange(newValue === ALL_OPTION_VALUE ? null : newValue);
            }}
            sx={{ minWidth: 180 }}
        >
            <MenuItem value={ALL_OPTION_VALUE}>Todos</MenuItem>
            {CLIENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
};

type HeaderSelectProps = {
    label: string;
    allLabel: string;
    options: Array<{ value: string; label: string }>;
    value: string | null;
    onChange: (value: string | null) => void;
};

const HeaderSelect = ({ label, allLabel, options, value, onChange }: HeaderSelectProps) => {
    return (
        <TextField
            select
            size="small"
            label={label}
            value={value === null ? ALL_OPTION_VALUE : value}
            onChange={(event) => {
                const newValue = event.target.value;
                onChange(newValue === ALL_OPTION_VALUE ? null : newValue);
            }}
            sx={{ minWidth: 160 }}
        >
            <MenuItem value={ALL_OPTION_VALUE}>{allLabel}</MenuItem>
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
};

export const SessionsTreatmentsDashboard = () => {
    const { values, setValue } = useGlobalFilterValues(sessionsTreatmentsGlobalFilters);
    const [avgChartClientType, setAvgChartClientType] = useState<string | null>(null);
    const [successChartClientType, setSuccessChartClientType] = useState<string | null>(null);
    const [outcomeGroupKey, setOutcomeGroupKey] = useState<CoarseDisorderGroupKey | null>(null);
    const [outcomeClientType, setOutcomeClientType] = useState<ClientTypeKey | null>(null);

    const now = new Date();
    const periodRange = resolveRequiredDateFilterRange(values, "period", "ultimos_13_meses", now);

    // Zoho "Próximos 12 meses" (separador Mês) = os 12 meses de calendário seguintes,
    // excluindo o mês corrente (validado via query_data: 791 exato).
    const next12MonthsRange = {
        gte: format(startOfMonth(addMonths(now, 1)), "yyyy-MM-dd"),
        lt: format(startOfMonth(addMonths(now, 13)), "yyyy-MM-dd"),
    };

    const startedResult = useAggregate(startedMonthlyQuery, {
        filter: { date_of_start: { _gte: periodRange.gte, _lt: periodRange.lt } },
    });
    const finishedResult = useAggregate(finishedMonthlyQuery, {
        filter: { date_if_finish: { _gte: periodRange.gte, _lt: periodRange.lt } },
    });

    // Tratamentos terminados no período (mínimo) → média de sessões + sucesso vs desistência.
    const finishedTreatmentsResult = useQuery({
        queryKey: ["sessions-treatments-finished-items", periodRange.gte, periodRange.lt],
        queryFn: () =>
            getItems("treatments", "id,date_if_finish,treatment_status_new,age_when_started", {
                filter: {
                    _and: [
                        { treatment_status_new: { _in: FINISHED_STATUSES } },
                        { date_if_finish: { _gte: periodRange.gte, _lt: periodRange.lt } },
                    ],
                },
                pageSize: 3000,
                sort: "date_if_finish",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const sessionsPerTreatmentResult = useAggregate(sessionsPerFinishedTreatmentQuery);

    // Análise global de tratamentos terminados (por gestora de caso): fetch rico dos terminados no
    // período + fotografia dos ativos (snapshot, ignora o "Período") — atribuição pela gestora de caso.
    const outcomeFinishedResult = useQuery({
        queryKey: ["sessions-treatments-outcome-finished", periodRange.gte, periodRange.lt],
        queryFn: () =>
            getItems("treatments", OUTCOME_TREATMENT_FIELDS, {
                filter: {
                    _and: [
                        { treatment_status_new: { _in: FINISHED_STATUSES } },
                        { date_if_finish: { _gte: periodRange.gte, _lt: periodRange.lt } },
                    ],
                },
                pageSize: 3000,
                sort: "date_if_finish",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const outcomeActiveResult = useQuery({
        queryKey: ["sessions-treatments-outcome-active"],
        queryFn: () =>
            getItems("treatments", OUTCOME_TREATMENT_FIELDS, {
                filter: { treatment_status_new: { _eq: "all_good" } },
                pageSize: 3000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });

    const futureMaioresResult = useAggregate(futureSessionsByServiceQuery, {
        filter: {
            _and: [
                { start_date: { _gte: next12MonthsRange.gte, _lt: next12MonthsRange.lt } },
                sessionClientTypeFilterByOption.maiores_18,
            ],
        },
    });
    const futureMenoresResult = useAggregate(futureSessionsByServiceQuery, {
        filter: {
            _and: [
                { start_date: { _gte: next12MonthsRange.gte, _lt: next12MonthsRange.lt } },
                sessionClientTypeFilterByOption.menores_igual_18,
            ],
        },
    });

    const activeChildrenResult = useAggregate(activeTreatmentsQuery, { filter: treatmentClientTypeFilterByOption.menores_igual_18 });
    const activeAdultsResult = useAggregate(activeTreatmentsQuery, { filter: treatmentClientTypeFilterByOption.maiores_18 });

    const partnersJunctionResult = useQuery({
        queryKey: ["sessions-treatments-partners-junction"],
        queryFn: () => getItems("professional_partners_treatments", "professional_partners_id,treatments_id", { pageSize: 3000, sort: "id" }),
        staleTime: 5 * 60 * 1000,
    });
    const partnersResult = useRecords("professional_partners", "id,entity,specialization");
    const treatmentsTotalResult = useAggregate(treatmentsTotalQuery);

    const startedFinishedPoints = monthlyPointsFromCountMaps([
        { key: "started", countsByMonthIndex: monthlyCountsFromGroupedRows(startedResult.data || [], "date_of_start") },
        { key: "finished", countsByMonthIndex: monthlyCountsFromGroupedRows(finishedResult.data || [], "date_if_finish") },
    ]);

    const finishedItems: Array<FinishedTreatmentItem> = finishedTreatmentsResult.data?.items || [];
    const sessionCountsByTreatment = sessionsByTreatment(sessionsPerTreatmentResult.data || [], NFB_SERVICE, PSY_SERVICE);

    const avgChartItems = finishedItems.filter((item) => treatmentMatchesClientType(item.age_when_started, avgChartClientType));
    const avgChartMonths = buildFinishedTreatmentAnalysis(avgChartItems, sessionCountsByTreatment, SUCCESS_STATUSES, DECLINED_STATUS);

    const successChartItems = finishedItems.filter((item) => treatmentMatchesClientType(item.age_when_started, successChartClientType));
    const successChartMonths = buildFinishedTreatmentAnalysis(successChartItems, sessionCountsByTreatment, SUCCESS_STATUSES, DECLINED_STATUS);

    const outcomeFilters: OutcomeFilters = { groupKey: outcomeGroupKey, clientType: outcomeClientType };
    const outcomeFinishedItems = (outcomeFinishedResult.data?.items || []) as Array<OutcomeTreatmentItem>;
    const outcomeActiveItems = (outcomeActiveResult.data?.items || []) as Array<OutcomeTreatmentItem>;
    const outcomeItems = outcomePivotItems(outcomeFinishedItems, outcomeActiveItems, outcomeFilters);
    const outcomePivotResult = outcomePivot(outcomeItems, outcomeClientType);
    const outcomeLoading = outcomeFinishedResult.isLoading || outcomeActiveResult.isLoading;
    const outcomeError = outcomeFinishedResult.isError || outcomeActiveResult.isError;

    const futurePivotTableRows = futureSessionsPivotRows(
        futureMaioresResult.data || [],
        futureMenoresResult.data || [],
        NFB_SERVICE,
        PSY_SERVICE
    );

    const partnersById: Record<number, { entity: string | null; specialization: string | null }> = Object.fromEntries(
        (partnersResult.data?.items || []).map(
            (partner: { id: number; entity: string | null; specialization: string | null }) => [
                partner.id,
                { entity: partner.entity, specialization: partner.specialization },
            ]
        )
    );
    const treatmentsTotal = singleCount(treatmentsTotalResult.data);
    const partnersJunctionRows = partnersJunctionResult.data?.items || [];
    const specializationTableRows = partnerSpecializationRows(partnersJunctionRows, partnersById, treatmentsTotal).map((row) => ({
        ...row,
        treatments: formatQuantity(row.treatments),
    }));
    const partnerNameTableRows = partnerNameRows(partnersJunctionRows, partnersById, treatmentsTotal).map((row) => ({
        ...row,
        treatments: formatQuantity(row.treatments),
    }));

    const isFinishedAnalysisLoading = finishedTreatmentsResult.isLoading || sessionsPerTreatmentResult.isLoading;
    const arePartnersLoading = partnersJunctionResult.isLoading || partnersResult.isLoading || treatmentsTotalResult.isLoading;

    const clientTypeExplanationSuffix =
        ' O "Tipo de cliente" baseia-se na idade no início do tratamento: "Maiores 18" = idade > 18; "Menores ou igual 18" = idade ≤ 18 ou sem idade registada.';
    const finishedCriteriaText = `Considera tratamentos com estado ${treatmentStatusListPt(FINISHED_STATUSES)}.`;

    const outcomeDrillColumns: Array<DrillDownColumn> = [
        { key: "clientNumber", label: "Nº cliente" },
        { key: "ownerName", label: "Gestora de caso" },
        { key: "clientTypeLabel", label: "Tipo" },
        { key: "disorderLabel", label: "Perturbação" },
        {
            key: "statusNew",
            label: "Estado",
            valueFor: (row: OutcomeDrillRow) => (row.statusNew ? TREATMENT_STATUS_LABELS_PT[row.statusNew] || row.statusNew : null),
        },
        { key: "dateOfStart", label: "Início", valueFor: (row: OutcomeDrillRow) => formatDrillDate(row.dateOfStart) },
        { key: "dateIfFinish", label: "Fim", valueFor: (row: OutcomeDrillRow) => formatDrillDate(row.dateIfFinish) },
        { key: "doneSessions", label: "Sessões realizadas", isNumeric: true },
    ];

    // Drill por célula: "Ativos" abre os ativos do nó, as restantes colunas abrem os terminados — o
    // universo deriva dos itens do próprio nó, por isso reconcilia com o número mostrado.
    const outcomeCellDrillDown = (node: PivotNode<OutcomeTreatmentItem>, columnKey: string) => {
        const isActivesColumn = columnKey === ACTIVES_MEASURE_KEY;
        const treatments = isActivesColumn ? activeTreatmentsOf(node.items) : finishedTreatmentsOf(node.items);
        if (treatments.length === 0) return null;
        const config: DrillDownConfig = {
            kind: "rows",
            title: `Tratamentos ${isActivesColumn ? "ativos" : "terminados"} — ${node.path.join(" — ")}`,
            columns: outcomeDrillColumns,
            rows: treatments.map(outcomeDrillRowOf),
            defaultSortKey: isActivesColumn ? "-dateOfStart" : "-dateIfFinish",
            rowRoute: treatmentRoute,
            queryDescription:
                `Um tratamento ${isActivesColumn ? "ativo" : "terminado"} por linha, com a perturbação (grupo), o tipo de cliente e as sessões de ` +
                "NeuroFeedback e Psicologia realizadas — mesmo universo e filtros da célula clicada, por isso a contagem reconcilia com o número mostrado.",
        };
        return config;
    };

    const explanations = {
        startedFinished: {
            queryDescription: `${describeTreatmentsCount({ dateFieldLabel: "data de início", range: periodRange })} ${describeTreatmentsCount({ dateFieldLabel: "data de fim", range: periodRange })} Sem filtros de estado — conta todos os tratamentos, agrupados por mês, no período selecionado.`,
        },
        avgSessions: {
            queryDescription: `Para cada tratamento terminado (mês da data de fim, no período selecionado), conta as sessões de NeuroFeedback e Psicologia do tratamento (excluindo sessões internas da equipa) e calcula a média por tratamento. ${finishedCriteriaText} Tratamentos sem sessões destes serviços não entram na média.${clientTypeExplanationSuffix}`,
        },
        outcome: {
            queryDescription:
                `Todos os tratamentos terminados no período (data de fim): sucesso (${treatmentStatusListPt(SUCCESS_STATUSES)}) ou desistência (${treatmentStatusListPt([DECLINED_STATUS])}), ` +
                `atribuídos à gestora de caso (dono do tratamento); tratamentos sem gestora contam em "Sem gestora de caso". ` +
                `Ativos = fotografia atual dos tratamentos com estado "Ativo" — NÃO segue o filtro "Período". ` +
                `% sucesso = sucessos ÷ (sucessos + desistências). Adultos = idade de início > 18; Crianças = ≤ 18 ou sem idade registada. ` +
                `Média sessões = média de sessões de NeuroFeedback e Psicologia realizadas por tratamento terminado. ` +
                `O grupo de perturbação vem do diagnóstico principal do tratamento (sem diagnóstico conta em "Outros"). ` +
                `Expande cada gestora para veres o detalhe por grupo de perturbação; clica numa célula para veres os tratamentos por trás do número.`,
            warningNote:
                "Visão global da empresa: ao contrário do dashboard de KPIs da equipa de serviço clínico, NÃO separa por serviço (NeuroFeedback/Psicologia) " +
                "nem exclui os tratamentos geridos pela própria psicóloga — conta todos os tratamentos terminados uma vez, pela gestora de caso.",
        },
        success: {
            queryDescription: `Por mês da data de fim (período selecionado): Sucesso = tratamentos com estado ${treatmentStatusListPt(SUCCESS_STATUSES)}; Desistência = estado ${treatmentStatusListPt([DECLINED_STATUS])}; % sucesso = Sucesso / (Sucesso + Desistência) × 100 (eixo direito).${clientTypeExplanationSuffix}`,
        },
        futureSessions: {
            queryDescription: `${describeSessionsCount({ services: [NFB_SERVICE, PSY_SERVICE], serviceNames: { [NFB_SERVICE]: "NeuroFeedback", [PSY_SERVICE]: "Psicologia" }, range: next12MonthsRange, dateFieldLabel: "data de início", excludesInternal: false, extraConditionText: `Considera apenas sessões de tratamentos com estado ${treatmentStatusListPt(["all_good"])}.` })} Linhas por tipo de cliente (idade no início do tratamento; sem idade registada conta como "Menores ou igual 18").`,
        },
        activeChildren: {
            queryDescription: describeTreatmentsCount({
                statuses: ["all_good"],
                extraConditionText: 'Apenas clientes "Menores ou igual 18" (idade no início ≤ 18 ou sem idade registada).',
            }),
        },
        activeAdults: {
            queryDescription: describeTreatmentsCount({
                statuses: ["all_good"],
                extraConditionText: 'Apenas clientes "Maiores 18" (idade no início > 18).',
            }),
        },
        partnersBySpecialization: {
            queryDescription:
                'Conta os tratamentos associados a cada especialização de parceiro profissional (junção parceiro–tratamento). "Sem parceiro" = total de tratamentos sem qualquer parceiro associado. Um tratamento com vários parceiros conta em todas as linhas aplicáveis.',
        },
        partnersByName: {
            queryDescription:
                'Conta os tratamentos associados a cada parceiro profissional (junção parceiro–tratamento). "Sem parceiro" = total de tratamentos sem qualquer parceiro associado.',
        },
    };

    return (
        <DashboardPage globalFilters={sessionsTreatmentsGlobalFilters} values={values} onChange={setValue}>
            <WidgetCard
                title="Tratamentos iniciados vs terminados"
                explanation={explanations.startedFinished}
                bodyHeight={380}
                isLoading={startedResult.isLoading || finishedResult.isLoading}
                isError={startedResult.isError || finishedResult.isError}
            >
                <AreaChart points={startedFinishedPoints} series={startedFinishedSeries} />
            </WidgetCard>

            <WidgetCard
                title="Média de sessões por tratamento terminado"
                explanation={explanations.avgSessions}
                bodyHeight={380}
                isLoading={isFinishedAnalysisLoading}
                isError={finishedTreatmentsResult.isError || sessionsPerTreatmentResult.isError}
                headerControls={<ClientTypeSelect value={avgChartClientType} onChange={setAvgChartClientType} />}
            >
                <TimeSeriesChart
                    points={avgChartMonths.map((month) => ({ monthLabel: month.monthLabel, avgSessions: month.avgSessions ?? 0 }))}
                    series={avgSessionsSeries}
                    showPointLabels={false}
                />
            </WidgetCard>

            <PivotTableCard
                title="Análise global de tratamentos terminados"
                explanation={explanations.outcome}
                pivot={outcomePivotResult}
                rowHeaderLabel="Gestora de caso / Perturbação"
                maxBodyHeight={480}
                headerControls={
                    <>
                        <HeaderSelect
                            label="Perturbação"
                            allLabel="Todas"
                            options={COARSE_DISORDER_GROUPS.map((group) => ({ value: group.key, label: group.label }))}
                            value={outcomeGroupKey}
                            onChange={(newValue) => setOutcomeGroupKey(newValue as CoarseDisorderGroupKey | null)}
                        />
                        <HeaderSelect
                            label="Tipo de cliente"
                            allLabel="Todos"
                            options={OUTCOME_CLIENT_TYPE_OPTIONS}
                            value={outcomeClientType}
                            onChange={(newValue) => setOutcomeClientType(newValue as ClientTypeKey | null)}
                        />
                    </>
                }
                isLoading={outcomeLoading}
                isError={outcomeError}
                cellDrillDown={outcomeCellDrillDown}
            />

            <WidgetCard
                title="Sucesso vs Desistência"
                explanation={explanations.success}
                bodyHeight={380}
                isLoading={isFinishedAnalysisLoading}
                isError={finishedTreatmentsResult.isError}
                headerControls={<ClientTypeSelect value={successChartClientType} onChange={setSuccessChartClientType} />}
            >
                <TimeSeriesChart
                    points={successChartMonths.map((month) => ({
                        monthLabel: month.monthLabel,
                        success: month.success,
                        declined: month.declined,
                        successPct: month.successPct,
                    }))}
                    series={successSeries}
                />
            </WidgetCard>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <DataTableCard
                        title="Sessões futuras por tipo de cliente"
                        explanation={explanations.futureSessions}
                        columns={[
                            { key: "clientType", label: "Tipo de cliente" },
                            { key: "nfb", label: "NeuroFeedback", isNumeric: true },
                            { key: "psy", label: "Psicologia", isNumeric: true },
                            { key: "total", label: "Sessões", isNumeric: true },
                        ]}
                        rows={futurePivotTableRows.slice(0, 2)}
                        totalsRow={futurePivotTableRows[2]}
                        isLoading={futureMaioresResult.isLoading || futureMenoresResult.isLoading}
                        isError={futureMaioresResult.isError || futureMenoresResult.isError}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column" gap={3} height="100%">
                        <StatCard
                            label="Crianças - ativos"
                            value={activeChildrenResult.data ? singleCount(activeChildrenResult.data) : undefined}
                            color={DASHBOARD_COLORS.done}
                            explanation={explanations.activeChildren}
                            isLoading={activeChildrenResult.isLoading}
                            drillDown={drillDownFromAggregate(activeTreatmentsQuery, treatmentClientTypeFilterByOption.menores_igual_18, {
                                title: "Crianças - ativos",
                                queryDescription: explanations.activeChildren.queryDescription,
                                fieldsQuery: TREATMENTS_DRILL_FIELDS,
                                defaultSort: "date_of_start",
                                columns: treatmentsDrillColumns,
                                rowRoute: treatmentRoute,
                            })}
                        />
                        <StatCard
                            label="Adultos - ativos"
                            value={activeAdultsResult.data ? singleCount(activeAdultsResult.data) : undefined}
                            color={DASHBOARD_COLORS.neutral}
                            explanation={explanations.activeAdults}
                            isLoading={activeAdultsResult.isLoading}
                            drillDown={drillDownFromAggregate(activeTreatmentsQuery, treatmentClientTypeFilterByOption.maiores_18, {
                                title: "Adultos - ativos",
                                queryDescription: explanations.activeAdults.queryDescription,
                                fieldsQuery: TREATMENTS_DRILL_FIELDS,
                                defaultSort: "date_of_start",
                                columns: treatmentsDrillColumns,
                                rowRoute: treatmentRoute,
                            })}
                        />
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <DataTableCard
                        title="Parceiros por especialidade"
                        explanation={explanations.partnersBySpecialization}
                        columns={[
                            { key: "specialization", label: "Especialização" },
                            { key: "treatments", label: "Tratamentos", isNumeric: true },
                        ]}
                        rows={specializationTableRows}
                        maxBodyHeight={420}
                        isLoading={arePartnersLoading}
                        isError={partnersJunctionResult.isError}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <DataTableCard
                        title="Parceiros por nome"
                        explanation={explanations.partnersByName}
                        columns={[
                            { key: "name", label: "Parceiro" },
                            { key: "specialization", label: "Especialização" },
                            { key: "treatments", label: "Tratamentos", isNumeric: true },
                        ]}
                        rows={partnerNameTableRows}
                        maxBodyHeight={420}
                        isLoading={arePartnersLoading}
                        isError={partnersJunctionResult.isError}
                    />
                </Grid>
            </Grid>
        </DashboardPage>
    );
};
